import { useEffect } from 'react'

import { navigate } from '@redwoodjs/router'

import { InsightsPageType } from 'src/components/InsightsCommon/types'
import { useHomePage } from 'src/hooks/use-home-page'
import {
  useMyOrg,
  useMyRecordTypes,
  useIsGovWellStaff,
  useIsUnrestrictedGov,
} from 'src/layouts/AppStateContextLayout/utils'
import { FeatureGate, useFeatureFlagging } from 'src/utils/featureFlagging/featureFlagging'

type Props = {
  insightsPageType: InsightsPageType
  shouldHandleRedirect?: boolean
}

export const useCanViewInsightsPage = ({ insightsPageType, shouldHandleRedirect }: Props) => {
  const isUnrestrictedGov = useIsUnrestrictedGov()
  const recordTypes = useMyRecordTypes()
  const org = useMyOrg()
  const homePage = useHomePage()
  const isGovWellStaff = useIsGovWellStaff()

  const featureFlagging = useFeatureFlagging()

  // Enable explicitly for GovWell staff to allow for testing
  const isInsightsAutoCheckPageEnabled =
    featureFlagging.checkFeatureFlag(FeatureGate.InsightsAutoCheckPage) || isGovWellStaff
  const isInsightsCommunityPageEnabled =
    featureFlagging.checkFeatureFlag(FeatureGate.InsightsCommunityPage) || isGovWellStaff
  const isInsightsInspectionsPageEnabled =
    featureFlagging.checkFeatureFlag(FeatureGate.InsightsInspectionsPage) || isGovWellStaff

  const canViewAutoCheckPage =
    isInsightsAutoCheckPageEnabled && !!org?.hasRecordTypeWithApplicationAutoCheckEnabled
  const canViewCommunityPage = isInsightsCommunityPageEnabled
  const canViewInspectionsPage =
    isInsightsInspectionsPageEnabled && recordTypes?.some((rt) => rt.allowInspections)

  const canViewInsights =
    isUnrestrictedGov &&
    (insightsPageType !== InsightsPageType.AutoCheck || canViewAutoCheckPage) &&
    (insightsPageType !== InsightsPageType.Community || canViewCommunityPage) &&
    (insightsPageType !== InsightsPageType.Inspections || canViewInspectionsPage)

  useEffect(() => {
    if (shouldHandleRedirect && !canViewInsights) {
      navigate(homePage)
    }
  }, [canViewInsights, homePage, shouldHandleRedirect])

  return canViewInsights
}
